import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl, formatApiError } from "@/lib/api";
import { Pencil, Trash2, Plus, LogOut, Star, ExternalLink, X, GripVertical } from "lucide-react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

const EMPTY_PROJECT = {
  title: "",
  description: "",
  cover_image: "",
  behance_url: "",
  category: "Motion Graphics",
  year: String(new Date().getFullYear()),
  featured: false,
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // project or 'new'
  const [form, setForm] = useState(EMPTY_PROJECT);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/projects?include_hidden=true");
      setProjects(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onLogout = () => {
    logout();
    nav("/");
  };

  const openNew = () => {
    setForm(EMPTY_PROJECT);
    setEditing("new");
  };

  const openEdit = (p) => {
    setForm({
      title: p.title || "",
      description: p.description || "",
      cover_image: p.cover_image || "",
      behance_url: p.behance_url || "",
      category: p.category || "Motion Graphics",
      year: p.year || "",
      featured: !!p.featured,
    });
    setEditing(p);
  };

  const closeDialog = () => {
    setEditing(null);
    setErr("");
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErr("");
    try {
      if (editing === "new") {
        await api.post("/projects", form);
      } else {
        await api.put(`/projects/${editing.id}`, form);
      }
      await load();
      closeDialog();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}"?`)) return;
    try {
      await api.delete(`/projects/${p.id}`);
      await load();
    } catch (e) {
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }));

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = projects.findIndex((p) => p.id === active.id);
    const newIndex = projects.findIndex((p) => p.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(projects, oldIndex, newIndex);
    setProjects(next); // optimistic
    try {
      await api.put("/projects/reorder/bulk", {
        ordered_ids: next.map((p) => p.id),
      });
    } catch (e) {
      await load(); // rollback to server state
      alert(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <main className="min-h-screen bg-ink text-bone" data-testid="admin-dashboard">
      {/* Top bar */}
      <div className="border-b border-ink-border px-6 md:px-12 py-5 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/" className="font-manrope text-xs uppercase tracking-[0.3em] text-bone/70 hover:text-bone">
            ← Site
          </Link>
          <span className="h-4 w-px bg-bone/20" />
          <span className="font-oswald uppercase tracking-tight text-xl">Admin · Projects</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="font-manrope text-xs text-bone/60 hidden md:block">
            {user?.email}
          </span>
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="flex items-center gap-2 border border-ink-border px-3 py-2 font-manrope text-xs uppercase tracking-[0.25em] text-bone/80 hover:bg-bone hover:text-ink transition-colors"
          >
            <LogOut className="h-3 w-3" /> Sign out
          </button>
        </div>
      </div>

      <div className="px-6 md:px-12 py-10 max-w-7xl mx-auto">
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="font-oswald uppercase tracking-tight text-5xl md:text-6xl">
              Projects
            </h1>
            <p className="font-manrope text-bone/60 text-sm mt-2">
              {projects.length} projects in the portfolio
            </p>
          </div>
          <button
            onClick={openNew}
            data-testid="new-project-button"
            className="flex items-center gap-2 bg-bone text-ink font-oswald uppercase tracking-tight px-5 py-3 hover:bg-bone/90 transition-colors"
          >
            <Plus className="h-4 w-4" /> New project
          </button>
        </div>

        {loading ? (
          <div className="font-manrope text-bone/60">Loading…</div>
        ) : (
          <div className="border border-ink-border overflow-hidden">
            <table className="w-full" data-testid="projects-table">
              <thead className="bg-ink-surface">
                <tr className="font-manrope text-[10px] uppercase tracking-[0.25em] text-bone/60 text-left">
                  <th className="px-2 py-3 w-8"></th>
                  <th className="px-4 py-3 w-12">#</th>
                  <th className="px-4 py-3 w-20">Cover</th>
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3 hidden md:table-cell">Category</th>
                  <th className="px-4 py-3 hidden md:table-cell">Year</th>
                  <th className="px-4 py-3 w-16 text-center">★</th>
                  <th className="px-4 py-3 w-32 text-right">Actions</th>
                </tr>
              </thead>
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                <SortableContext items={projects.map((p) => p.id)} strategy={verticalListSortingStrategy}>
                  <tbody>
                    {projects.map((p, i) => (
                      <SortableRow
                        key={p.id}
                        project={p}
                        index={i}
                        onEdit={openEdit}
                        onDelete={onDelete}
                      />
                    ))}
                    {projects.length === 0 && (
                      <tr>
                        <td colSpan={8} className="px-4 py-12 text-center font-manrope text-bone/50">
                          No projects yet. Create the first one.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </SortableContext>
              </DndContext>
            </table>
            <div className="px-4 py-2 bg-ink-surface border-t border-ink-border font-manrope text-[10px] uppercase tracking-[0.25em] text-bone/50">
              Drag projects to reorder
            </div>
          </div>
        )}
      </div>

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          data-testid="project-dialog"
          onClick={closeDialog}
        >
          <div
            className="bg-ink border border-ink-border w-full max-w-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-ink-border">
              <h2 className="font-oswald uppercase tracking-tight text-2xl">
                {editing === "new" ? "New project" : "Edit project"}
              </h2>
              <button
                onClick={closeDialog}
                data-testid="close-dialog"
                className="p-2 hover:bg-ink-surface transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <form onSubmit={onSubmit} className="p-6 space-y-4">
              <Field
                label="Title"
                value={form.title}
                onChange={(v) => setForm({ ...form, title: v })}
                testid="form-title"
                required
              />
              <Field
                label="Image URL (cover)"
                value={form.cover_image}
                onChange={(v) => setForm({ ...form, cover_image: v })}
                testid="form-cover"
                required
              />
              {form.cover_image ? (
                <div className="aspect-video bg-ink-surface border border-ink-border overflow-hidden">
                  <img src={mediaUrl(form.cover_image)} alt="preview" className="h-full w-full object-cover" />
                </div>
              ) : null}
              <Field
                label="Behance URL"
                value={form.behance_url}
                onChange={(v) => setForm({ ...form, behance_url: v })}
                testid="form-behance"
              />
              <div className="grid grid-cols-2 gap-4">
                <Field
                  label="Category"
                  value={form.category}
                  onChange={(v) => setForm({ ...form, category: v })}
                  testid="form-category"
                />
                <Field
                  label="Year"
                  value={form.year}
                  onChange={(v) => setForm({ ...form, year: v })}
                  testid="form-year"
                />
              </div>
              <div>
                <label className="block font-manrope text-[10px] uppercase tracking-[0.3em] text-bone/60 mb-2">
                  Description
                </label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="form-description"
                  rows={3}
                  className="w-full bg-ink-surface border border-ink-border text-bone px-4 py-3 font-manrope text-sm focus:outline-none focus:border-bone"
                />
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  data-testid="form-featured"
                  className="h-4 w-4 accent-bone"
                />
                <span className="font-manrope text-sm text-bone/80">Featured</span>
              </label>

              {err && (
                <div className="font-manrope text-sm text-red-400 border border-red-900/40 bg-red-950/30 px-4 py-3">
                  {err}
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeDialog}
                  className="px-5 py-2 border border-ink-border font-manrope text-sm uppercase tracking-[0.2em] hover:bg-ink-surface"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="submit-project"
                  className="px-6 py-2 bg-bone text-ink font-oswald uppercase tracking-tight disabled:opacity-60"
                >
                  {submitting ? "Saving…" : editing === "new" ? "Create" : "Save"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
}

function Field({ label, value, onChange, testid, required }) {
  return (
    <div>
      <label className="block font-manrope text-[10px] uppercase tracking-[0.3em] text-bone/60 mb-2">
        {label}
      </label>
      <input
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full bg-ink-surface border border-ink-border text-bone px-4 py-3 font-manrope text-sm focus:outline-none focus:border-bone transition-colors"
      />
    </div>
  );
}

function SortableRow({ project: p, index: i, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: p.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    background: isDragging ? "rgba(250,250,250,0.04)" : undefined,
    opacity: isDragging ? 0.85 : 1,
  };
  return (
    <tr
      ref={setNodeRef}
      style={style}
      data-testid={`row-${p.id}`}
      className="border-t border-ink-border hover:bg-ink-surface/50 transition-colors"
    >
      <td className="px-2 py-3 align-middle">
        <button
          type="button"
          data-testid={`drag-handle-${p.id}`}
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-bone/40 hover:text-bone p-1"
          title="Arrastrar para reordenar"
        >
          <GripVertical className="h-4 w-4" />
        </button>
      </td>
      <td className="px-4 py-3 font-manrope text-bone/50 text-sm">
        {String(i + 1).padStart(2, "0")}
      </td>
      <td className="px-4 py-3">
        {p.cover_image ? (
          <img
            src={mediaUrl(p.cover_image)}
            alt={p.title}
            className="h-10 w-14 object-cover border border-ink-border"
          />
        ) : (
          <div className="h-10 w-14 bg-ink-surface border border-ink-border" />
        )}
      </td>
      <td className="px-4 py-3">
        <div className="font-oswald uppercase tracking-tight text-base">{p.title}</div>
        {p.behance_url ? (
          <a
            href={p.behance_url}
            target="_blank"
            rel="noreferrer"
            className="font-manrope text-[11px] text-bone/50 hover:text-bone inline-flex items-center gap-1 mt-1"
          >
            Behance <ExternalLink className="h-3 w-3" />
          </a>
        ) : null}
      </td>
      <td className="px-4 py-3 hidden md:table-cell font-manrope text-sm text-bone/70">
        {p.category}
      </td>
      <td className="px-4 py-3 hidden md:table-cell font-manrope text-sm text-bone/70">
        {p.year}
      </td>
      <td className="px-4 py-3 text-center">
        {p.featured ? (
          <Star className="h-4 w-4 inline text-bone fill-bone" />
        ) : (
          <span className="text-bone/20">—</span>
        )}
      </td>
      <td className="px-4 py-3">
        <div className="flex items-center justify-end gap-2">
          <button
            onClick={() => onEdit(p)}
            data-testid={`edit-${p.id}`}
            className="p-2 border border-ink-border hover:bg-bone hover:text-ink transition-colors"
            title="Edit"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(p)}
            data-testid={`delete-${p.id}`}
            className="p-2 border border-ink-border hover:bg-red-600 hover:text-bone hover:border-red-600 transition-colors"
            title="Delete"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}
